import { AxiosInstance } from 'axios';
import { Result } from 'services/Result';
import { LegendModelGET } from 'types/admin/trackmanParameters';
import {
  CreateUpdateTrackmanParameterV2Request,
  ReorderTrackmanParametersV2Request,
  TrackmanParameterV2Content,
  TrackmanParameterV2Details,
  TrackmanParameterV2Overview,
} from 'types/admin/trackmanParametersV2';

const baseUrl = '/api/admin/v2/trackman-parameters';
const legendImagesUrl = '/api/admin/legend-images';

export class TrackmanParametersV2Service {
  private axiosInstance: AxiosInstance;
  constructor(axiosInstance: AxiosInstance) {
    this.axiosInstance = axiosInstance;
  }

  getParameters = async () => {
    const response = await this.axiosInstance.get<Result<TrackmanParameterV2Overview[]>>(baseUrl);
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while getting trackman parameters.');
  };

  getParameter = async (id: string) => {
    const response = await this.axiosInstance.get<Result<TrackmanParameterV2Details>>(`${baseUrl}/${id}`);
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while getting trackman parameter.');
  };

  createParameter = async (request: CreateUpdateTrackmanParameterV2Request) => {
    const response = await this.axiosInstance.post<Result<string>>(baseUrl, request);
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while creating trackman parameter.');
  };

  updateParameter = async (request: CreateUpdateTrackmanParameterV2Request) => {
    const response = await this.axiosInstance.put(baseUrl, request);
    if (response.status === 200) {
      return null;
    }
    throw Error('There was an internal error while updating trackman parameter.');
  };

  deleteParameter = async (id: string) => {
    await this.axiosInstance.delete(`${baseUrl}/${id}`);
  };

  reorderParameters = async (request: ReorderTrackmanParametersV2Request) => {
    await this.axiosInstance.post(`${baseUrl}/reorder`, request);
  };

  sendToTranslation = async (id: string) => {
    await this.axiosInstance.post(`${baseUrl}/${id}/send-to-translation`);
  };

  resyncTranslation = async (id: string, language: string) => {
    await this.axiosInstance.post(`${baseUrl}/${id}/resync-translation/${language}`);
  };

  getTranslatedContent = async (id: string, language: string) => {
    const response = await this.axiosInstance.get<Result<TrackmanParameterV2Content>>(`${baseUrl}/${id}/translated/${language}`);
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while getting translated trackman parameter.');
  };

  uploadMainImage = async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await this.axiosInstance.post<Result<string>>(`${baseUrl}/main-image`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while uploading the main image.');
  };

  getLegendImages = async () => {
    const response = await this.axiosInstance.get<Result<LegendModelGET[]>>(legendImagesUrl);
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while getting legend images.');
  };

  uploadLegendImage = async (file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    const response = await this.axiosInstance.post<Result<string>>(legendImagesUrl, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    if (response.data && !response.data.isError && response.data.data) {
      return response.data.data;
    }
    throw Error('There was an internal error while uploading legend image.');
  };
}

export default TrackmanParametersV2Service;
